import type { CSSProperties } from 'react'
import { JSX, useCallback, useEffect, useRef } from 'react'
import { observer } from 'mobx-react'
import { createPortal } from 'react-dom'
import useClickOutside from '@hooks/useClickOutside'
import './Popup.css'
import { ClassName, cx } from '@/utils/cx'
import { Icon } from '../Icon/Icon'

interface PopupProps {
  closeIconHidden?: boolean
  /** @default true — matches production behavior where all themes set clickOutsideEnabled: true */
  clickOutsideEnabled?: boolean
  title?: string
  /** Accessible label for the close button. Should be a localized string. @default 'Close' */
  closeLabel?: string
  footer?: React.ReactNode
  children: React.ReactNode
  handleVisibility: (bool: boolean) => void
  visibility: boolean
  closeIcon?: React.ReactNode
  topContainer?: JSX.Element
  style?: CSSProperties
  className?: ClassName
}

export const Popup = observer(
  ({
    children,
    title,
    closeLabel = 'Close',
    footer,
    closeIconHidden,
    clickOutsideEnabled = true,
    handleVisibility,
    visibility,
    closeIcon,
    topContainer,
    style,
    className,
  }: PopupProps) => {
    const popup = useRef<HTMLDivElement>(null)

    const close = useCallback(() => handleVisibility(false), [handleVisibility])
    useClickOutside(popup, clickOutsideEnabled ? close : undefined)

    useEffect(() => {
      if (visibility && popup.current) {
        popup.current.focus()
      }
    }, [visibility])

    return visibility ? (
      createPortal(
        <div className="popup__fullscreen-wrap onboarding-popup-wrap">
          <div
            ref={popup}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'popup-title' : undefined}
            aria-label={title ? undefined : 'Popup'}
            tabIndex={-1}
            className={cx('popup__container', 'onboarding-popup', className)}
            style={style}
          >
            {title && (
              <header className="popup__popup-header onboarding-popup-header">
                <h2 id="popup-title" className="popup__popup-title onboarding-popup-title">
                  {title}
                </h2>
              </header>
            )}

            {children && (
              <section
                className={cx(
                  'popup__popup-body',
                  !closeIconHidden && !title?.length && 'popup__popup-body--no-title'
                )}
              >
                {topContainer && <div className="popup__top-container">{topContainer}</div>}

                {children}
              </section>
            )}

            {footer && <footer className="popup__popup-footer">{footer}</footer>}

            {!closeIconHidden && (
              <button
                className="popup__close"
                aria-label={closeLabel}
                type="button"
                onClick={() => handleVisibility(false)}
              >
                {closeIcon ? closeIcon : <Icon size="24px" type="x-mark" />}
              </button>
            )}
          </div>
        </div>,
        document.body
      )
    ) : (
      <></>
    )
  }
)
